import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { ModuleChoices } from "@/lib/athleteHub";

type Answers = Record<string, string>;
const TEXT_QS: { key: string; label: string; hint?: string; long?: boolean; required?: boolean }[] = [
  { key: "known_as", required: true, label: "What name do fans know you by?" },
  { key: "sport_team_position", required: true, label: "Your sport, team and position" },
  { key: "one_sentence", required: true, label: "In one sentence, what do you want people to know about you?", long: true },
  { key: "three_words", label: "Three words teammates would use to describe you" },
  { key: "proud_moment", label: "A proud moment or achievement", long: true },
  { key: "why_play", required: true, label: "Why do you play?", long: true },
  { key: "causes", label: "Causes or communities you care about", long: true },
  { key: "socials", label: "Your social handles", hint: "Instagram, TikTok, YouTube or others" },
];
const INTERESTS = ["Sponsorships", "Appearances", "Speaking", "Collaborations"];

/** Quick Start: short intake; SIIS turns answers into draft copy. Stored privately; never edits the public profile. */
const AthleteQuickStart = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [answers, setAnswers] = useState<Answers>({});
  const [interests, setInterests] = useState<string[]>([]);
  const [choices, setChoices] = useState<ModuleChoices>({});
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);

  useEffect(() => {
    if (!user) return;
    supabase.rpc("athlete_my_hub").then(({ data, error }) => {
      if (error) return toast.error(error.message);
      const h = data as any;
      if (!h) { setReady(true); return; }
      const qs = h.quick_start ?? {};
      setAnswers(Object.fromEntries(Object.entries(qs).filter(([, v]) => typeof v === "string")) as Answers);
      setInterests(Array.isArray(qs.interests) ? qs.interests : []);
      setChoices(h.module_choices ?? {});
      setSubmitted(Boolean(h.quick_start_submitted_at));
      setReady(true);
    });
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/athlete-login" replace />;

  const save = async (submit: boolean) => {
    setBusy(true);
    const payload = { ...answers, interests };
    const ch: ModuleChoices = { ...choices, work_with: interests.length > 0, highlights: true, causes: Boolean(answers.causes?.trim()) };
    const { data, error } = await supabase.rpc("athlete_save_quick_start", { _answers: payload as any, _choices: ch as any, _submit: submit });
    setBusy(false);
    if (error) return toast.error(error.message);
    const res = data as { submitted?: boolean; missing?: string[] };
    const miss = res?.missing ?? [];
    setMissing(miss);
    if (submit && miss.length) {
      toast.error("Your draft is saved. Please answer the required questions marked below before submitting.");
      document.getElementById(`qs-${miss[0]}`)?.focus();
      return;
    }
    toast.success(submit ? "Sent to SIIS — we'll draft your profile." : "Draft saved. You can come back any time.");
    if (submit) nav("/dashboard");
  };

  const field = "mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm";
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 mx-auto w-full max-w-xl px-4 py-8">
        <Link to="/dashboard" className="text-sm text-muted-foreground">← Back to dashboard</Link>
        <h1 className="mt-3 font-display text-3xl">Quick Start</h1>
        <p className="mt-2 text-xs text-muted-foreground">* Required to submit. You can save a draft any time.</p>
        <p className="mt-2 text-sm text-muted-foreground">About 5 minutes. Answer in your own words — SIIS will turn this into your profile and you'll approve it before anything goes live.</p>
        {submitted && <p className="mt-3 rounded-lg bg-muted p-3 text-sm">You've already sent this to SIIS. You can still update your answers.</p>}
        {!ready ? <p className="mt-6 text-sm text-muted-foreground">Loading…</p> : (
          <form className="mt-6 space-y-5" onSubmit={(e) => { e.preventDefault(); save(true); }}>
            {TEXT_QS.map((q) => (
              <label key={q.key} className="block text-sm font-medium">{q.label}{q.required && <span className="text-primary"> *</span>}
                {q.hint && <span className="block text-xs font-normal text-muted-foreground">{q.hint}</span>}
                {q.long
                  ? <textarea id={`qs-${q.key}`} aria-invalid={missing.includes(q.key)} className={field} rows={3} maxLength={1000} value={answers[q.key] ?? ""} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })} />
                  : <input id={`qs-${q.key}`} aria-invalid={missing.includes(q.key)} className={field} maxLength={200} value={answers[q.key] ?? ""} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })} />}
                {missing.includes(q.key) && <span role="alert" className="mt-1 block text-xs text-destructive">Required before you can submit.</span>}
              </label>
            ))}

            <fieldset className="text-sm"><legend className="font-medium">Interested in working with brands? (choose any)</legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {INTERESTS.map((i) => (
                  <label key={i} className="flex items-center gap-2"><Checkbox checked={interests.includes(i)} onCheckedChange={(v) => setInterests(v ? [...interests, i] : interests.filter((x) => x !== i))} />{i}</label>
                ))}
              </div>
            </fieldset>

            <fieldset className="text-sm"><legend className="font-medium">Want fans to be able to support you directly?<span className="text-primary"> *</span></legend>
              {missing.includes("direct_support") && <span role="alert" id="qs-direct_support" tabIndex={-1} className="block text-xs text-destructive">Please choose one before submitting.</span>}
              <div className="mt-2 flex gap-4">
                {[["Yes", true], ["Not now", false]].map(([l, v]) => (
                  <label key={String(l)} className="flex items-center gap-2"><input type="radio" name="ds" checked={choices.direct_support === v} onChange={() => setChoices({ ...choices, direct_support: v as boolean })} />{l as string}</label>
                ))}
              </div>
            </fieldset>

            <fieldset className="text-sm"><legend className="font-medium">Interested in your own merchandise with SIIS?<span className="text-primary"> *</span></legend>
              {missing.includes("her_collection") && <span role="alert" id="qs-her_collection" tabIndex={-1} className="block text-xs text-destructive">Please choose one before submitting.</span>}
              <p className="text-xs text-muted-foreground">This only tells SIIS you're interested. Nothing is set up or sold until you and SIIS agree separately.</p>
              <div className="mt-2 flex gap-4">
                {[["Interested", "interested"], ["Not now", "not_now"]].map(([l, v]) => (
                  <label key={v} className="flex items-center gap-2"><input type="radio" name="hc" checked={choices.her_collection === v} onChange={() => setChoices({ ...choices, her_collection: v as any })} />{l}</label>
                ))}
              </div>
            </fieldset>

            <p className="text-xs text-muted-foreground">Add or confirm your profile photo in the Profile tab of your dashboard. Consent is confirmed on your dashboard Home.</p>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={busy}>Submit to SIIS</Button>
              <Button type="button" variant="outline" disabled={busy} onClick={() => save(false)}>Save draft</Button>
            </div>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AthleteQuickStart;
